<template>
  <div
    class="ingredient-attachments-view"
    data-cy="ingredient-attachments-view"
  >
    <div
      v-if="existingAttachments.length"
      class="ingredient-attachments-view__container"
    >
      <div
        class="ingredient-attachments-view__header"
        data-cy="ingredient-attachments-header"
      >
        <h3>Uploaded Attachments</h3>
      </div>
      <div class="ingredient-attachments-view__attachments">
        <div
          v-for="attachment in existingAttachments"
          :key="attachment.Id"
          class="ingredient-attachments-view__attachment"
          data-cy="ingredient-attachment"
        >
          <Button
            type="button"
            theme-color="primary"
            @click="downloadAttachmentAsync(attachment)"
            data-cy="download-attachment-button"
          >
            {{ attachment.Name }}{{ attachment.DocType }}
          </Button>
          <div
            class="ingredient-attachments-view__attachment__date"
            data-cy="attachment-date"
          >
            {{
              attachment.Created &&
              new Date(attachment.Created).toLocaleString()
            }}
          </div>
          <div class="ingredient-attachments-view__attachment__actions">
            <Button
              type="button"
              theme-color="error"
              @click="deleteAttachmentAsync(attachment)"
              data-cy="delete-attachment-button"
            >
              Delete
            </Button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import type { Attachment } from "@/Domain/Attachment";
import { api } from "@/api";
import { handleResponsePromiseAsync } from "@/shared/helpers/apiHelper";
import { onBeforeMount, ref } from "vue";
import { Button } from "@progress/kendo-vue-buttons";
import { useMainStore } from "@/stores/useMainStore";

const main = useMainStore();

const props = defineProps<{
  ingredientCartId: number;
}>();

const existingAttachments = ref<Attachment[]>([]);

const getIngredientAttachmentsAsync = async function () {
  return api.get("GetAttachmentsForIngredient", {
    ingredientCartId: props.ingredientCartId,
  });
};

const updateIngredientAttachmentsAsync = async function () {
  return handleResponsePromiseAsync(
    getIngredientAttachmentsAsync(),
    (data) => {
      existingAttachments.value = data;
    },
    "IngredientAttachmentsView.vue",
    "updateIngredientAttachmentsAsync"
  );
};

const downloadAttachmentAsync = async function (attachment: Attachment) {
  const response = await fetch(
    `data:${attachment.DocType};base64,${attachment.Data}`
  );
  const blob = await response.blob();

  const link = document.createElement("a");
  link.href = URL.createObjectURL(blob);
  link.download = attachment.Name + attachment.DocType;
  link.click();
  URL.revokeObjectURL(link.href);
};

const deleteAttachmentAsync = async function (attachment: Attachment) {
  await handleResponsePromiseAsync(
    api.get("DeleteIngredientAttachment", {
      userLogin: main.userLogin,
      companyLogin: main.companyLogin,
      ingredientId: props.ingredientCartId,
      attachmentName: attachment.Name + attachment.DocType,
    }),
    (data) => {
      if (data)
        existingAttachments.value.splice(
          existingAttachments.value.indexOf(attachment),
          1
        );
    },
    "IngredientAttachmentsView.vue",
    "deleteAttachment"
  );
};

onBeforeMount(async () => {
  await updateIngredientAttachmentsAsync();
});
</script>

<style lang="scss">
.ingredient-attachments-view {
  display: flex;
  flex-direction: column;
  width: 100%;

  &__header {
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 1rem;
  }

  &__attachments {
    display: flex;
    flex-direction: column;
    width: 100%;
    height: 100%;
    overflow-y: auto;
  }

  &__attachment {
    display: flex;
    flex-direction: row;
    justify-content: space-around;
    align-items: center;
    margin-bottom: 1rem;
    padding: 0.5rem 1rem;
    border: 1px solid #ccc;
    border-radius: 0.5rem;
    flex-wrap: wrap;
    gap: 4px;

    &__date {
      font-weight: bold;
    }
  }
}
</style>
